import React, { KeyboardEvent, ReactNode, useEffect, useState } from "react";

import FocusTrap from "focus-trap-react/dist/focus-trap-react";
import { createPortal } from "react-dom";

import { CloseIcon } from "@/components/icons/CloseIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

import styles from "./ModalNova.module.css";
import {
  Backdrop,
  CloseTrigger,
  Dialog,
  ModalBody,
  ModalHeader,
  ScrollWrapper,
} from "./ModalNova.styled";

const sizes: Record<string, string> = {
  small: "570px",
  medium: "900px",
  large: "1096px",
};

const DEFAULT_BACKDROP_Z_INDEX = 10000;
const DEFAULT_DIALOG_Z_INDEX = 10001;

export type ModalNovaProps = {
  className?: string;
  hideModal: () => void;
  title?: string;
  children?: ReactNode;
  size?: "small" | "medium" | "large" | string;
  fullHeightMobile?: boolean;
  fullWidthMobile?: boolean;
  fitContentMobile?: boolean;
  /**
   * Mobile only: "center" keeps the default placement, "bottom" attaches the modal to the bottom
   * of the page as a drawer as tall as its content.
   */
  position?: "center" | "bottom";
  /** Below md: false = centered content-height dialog; true (default) = 3/4 bottom sheet. */
  fixedDialog?: boolean;
  hasBodyPadding?: boolean;
  hasHeader?: boolean;
  /** Optional z-index for backdrop; dialog uses this + 1. When omitted, defaults apply (10000 / 10001). */
  customZIndex?: number;
  backdropClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  hideCloseTrigger?: boolean;
  disableBackdropClick?: boolean;
};

export const ModalNova: React.FC<ModalNovaProps> = ({
  className = "modal-nova",
  hideModal,
  title,
  children,
  size = "medium",
  fullHeightMobile = false,
  fullWidthMobile = false,
  fitContentMobile = false,
  position = "center",
  fixedDialog = true,
  hasBodyPadding = true,
  hasHeader = true,
  customZIndex,
  backdropClassName,
  headerClassName,
  bodyClassName,
  hideCloseTrigger = false,
  disableBackdropClick = false,
}) => {
  const backdropZIndex = customZIndex ?? DEFAULT_BACKDROP_Z_INDEX;
  const dialogZIndex = customZIndex != null ? customZIndex + 1 : DEFAULT_DIALOG_Z_INDEX;
  const [isMobile, setIsMobile] = useState(true);

  // Combined useEffect for mounting/unmounting logic and scroll lock
  useEffect(() => {
    // CAPTURE SCROLL POSITION
    const currentScrollY = window.scrollY;

    // APPLY NO-SCROLL FIX with inline styles
    document.body.classList.add("no-scroll");
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${currentScrollY}px`;
    document.body.style.width = "100%";

    // Existing media query logic
    const mediaQuery = window.matchMedia("(min-width: 768px)");

    if (mediaQuery.matches) {
      setIsMobile(false);
    }

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setIsMobile(!e.matches);
    };

    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      // 3. RESTORE SCROLL POSITION AND CLEANUP
      document.body.classList.remove("no-scroll");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("position");
      document.body.style.removeProperty("top");
      document.body.style.removeProperty("width");

      window.scrollTo(0, currentScrollY);

      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, []);

  // NEW useEffect for Escape Key global listener
  useEffect(() => {
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        hideModal();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [hideModal]);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter") {
      hideModal();
    }
  };

  useEffect(() => {
    // Hide Ada icon behind modal on mobile
    const adaChat = document.getElementById("ada-button-frame");
    if (adaChat) {
      adaChat.style.display = isMobile ? "none" : "block";
    }
  }, [isMobile]);

  const CloseTriggerWrap = () =>
    hideCloseTrigger ? null : (
      <CloseTrigger
        className="close-trigger"
        tabIndex={0}
        onClick={() => {
          hideModal();
        }}
        onKeyDown={handleKeyDown}
      >
        <CloseIcon className={`mx-auto`} />
      </CloseTrigger>
    );

  const modalContent = (
    <FocusTrap focusTrapOptions={{ fallbackFocus: "#Modal" }}>
      <div>
        <Backdrop
          className={cn(backdropClassName, disableBackdropClick && "pointer-events-none")}
          fullHeightMobile={fullHeightMobile}
          onClick={() => {
            if (!disableBackdropClick) hideModal();
          }}
          style={{ zIndex: backdropZIndex }}
        />
        <Dialog
          open
          className={cn(
            className,
            fullHeightMobile ? styles.mobileFullscreen : "",
            fullWidthMobile ? styles.mobileFullWidth : "",
            fitContentMobile ? styles.mobileFitContent : "",
            position === "bottom" ? styles.mobilePositionBottom : "",
            !fixedDialog && styles.normalDialog
          )}
          id="Modal"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          maxWidth={sizes[size] ? sizes[size] : size}
          style={{ zIndex: dialogZIndex }}
        >
          <ScrollWrapper className={``}>
            {hasHeader ? (
              <ModalHeader className={cn("modal-nova-header", headerClassName)} hasTitle={!!title}>
                {title ? (
                  <TextElementNova
                    text={title}
                    className="Body-30-Dia-Strong text-nova-base-black flex-1"
                  />
                ) : null}
                <CloseTriggerWrap />
              </ModalHeader>
            ) : (
              <div className="pt-6 px-6 md:p-0">
                <CloseTriggerWrap />
              </div>
            )}
            <ModalBody
              className={cn("modal-nova-body", bodyClassName)}
              hasBodyPadding={hasBodyPadding}
            >
              {children}
            </ModalBody>
          </ScrollWrapper>
        </Dialog>
      </div>
    </FocusTrap>
  );

  if (typeof document === "undefined") return modalContent;
  return createPortal(modalContent, document.body);
};
